import { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Smartphone, Loader2, KeyRound } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { coreCall } from '../../lib/services/gatewayService';
import { TwoFactorCodeInputs } from './TwoFactorCodeInputs';
import { emptyCode } from '../../lib/twofa/codeInput';
import { initialMethod, interpretSendEmailResponse, shouldAutoSendEmail, type TwoFactorMethod } from '../../lib/twofa/emailCode';
import { readFunctionError, rememberThisDevice } from '../../lib/twofa/twofaApi';
import { CONNECTION_ERROR_MESSAGE, CONNECTION_ERROR_TIPS, SUPPORT_EMAIL, isConnectionError } from '../../lib/twofa/connectionError';

interface TwoFactorVerificationProps {
  email: string;
  availableMethods: TwoFactorMethod[];
  onVerify: (sessionToken: string) => void;
  onCancel: () => void;
  embedded?: boolean; // true = senza riquadro modale (login e SSO)
}

/**
 * Passo del codice della verifica in due passaggi (login password e SSO), stile Notion.
 * - email: codice inviato da solo all'apertura (una volta), "Reinvia" dopo 60 secondi (limite del server)
 * - incolla/compilazione automatica di 6 cifre e verifica appena il codice e' completo
 * - "Ricorda questo dispositivo per 30 giorni" (token salvato nel browser, impronta sul server)
 * - "Usa un codice di emergenza" (monouso, consumato dal server)
 */
export function TwoFactorVerification({ email, availableMethods, onVerify, onCancel, embedded = false }: TwoFactorVerificationProps) {
  const [method, setMethod] = useState<TwoFactorMethod>(initialMethod(availableMethods));
  const [mode, setMode] = useState<'code' | 'backup'>('code');
  const [code, setCode] = useState<string[]>(emptyCode());
  const [backupCode, setBackupCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // L'errore e' di collegamento (la richiesta non e' arrivata al server): sotto si mostra cosa provare
  const [connectionTips, setConnectionTips] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const emailRequestedRef = useRef(false);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const sendEmailCode = useCallback(async () => {
    setSendingEmail(true);
    setError(null);
    setConnectionTips(false);
    setInfo(null);
    try {
      const { data, error: sendError } = await coreCall('twofa-send-email-code', { body: { email } });
      if (sendError) throw sendError;
      const outcome = interpretSendEmailResponse(data);
      setCountdown(outcome.countdown);
      if (outcome.isError) setError(outcome.message);
      else setInfo(outcome.message ?? `Codice inviato a ${email}`);
    } catch (err) {
      console.error('[2FA] Invio codice email fallito:', err);
      if (isConnectionError(err)) {
        setError(CONNECTION_ERROR_MESSAGE.send);
        setConnectionTips(true);
      } else {
        setError((err as { message?: string })?.message || "Errore nell'invio del codice. Riprova.");
      }
    } finally {
      setSendingEmail(false);
    }
  }, [email]);

  // Invio automatico del codice email all'apertura del passo (o al passaggio all'email), una volta sola
  useEffect(() => {
    if (shouldAutoSendEmail(method, emailRequestedRef.current)) {
      emailRequestedRef.current = true;
      void sendEmailCode();
    }
  }, [method, sendEmailCode]);

  const verify = async (value: string, verifyMethod: TwoFactorMethod | 'backup') => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setLoading(true);
    setError(null);
    setConnectionTips(false);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('twofa-verify-code', {
        body: { email, code: value, method: verifyMethod, rememberDevice: remember },
      });
      if (fnError && isConnectionError(fnError)) throw fnError;
      if (fnError) throw new Error(await readFunctionError(fnError, 'Codice non valido. Riprova.'));
      if (!data?.success || !data.twoFactorSessionToken) throw new Error(data?.error || 'Codice non valido. Riprova.');
      if (data.trustedDeviceToken && data.trustedDeviceExpiresAt) {
        await rememberThisDevice(data.trustedDeviceToken, data.trustedDeviceExpiresAt);
      }
      onVerify(data.twoFactorSessionToken);
    } catch (err) {
      if (isConnectionError(err)) {
        // Il codice non e' arrivato al server: si lascia com'e', cosi' basta ripremere Verifica
        setError(CONNECTION_ERROR_MESSAGE.verify);
        setConnectionTips(true);
        return;
      }
      setError((err as Error).message || 'Codice non valido. Riprova.');
      if (verifyMethod === 'backup') setBackupCode('');
      else setCode(emptyCode());
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const switchMethod = (next: TwoFactorMethod) => {
    setMethod(next);
    setCode(emptyCode());
    setError(null);
    setInfo(null);
  };

  const busy = loading || sendingEmail;
  const subtitle = mode === 'backup'
    ? 'Inserisci uno dei codici di emergenza che hai salvato. Ogni codice vale una volta sola.'
    : method === 'totp'
      ? "Inserisci il codice a 6 cifre dell'app di autenticazione."
      : `Inserisci il codice a 6 cifre inviato a ${email}.`;

  const content = (
    <div className="text-left">
      <h2 className="text-lg font-semibold text-[#37352F]">Verifica in due passaggi</h2>
      <p className="text-sm text-[#787774] mt-1 mb-5">{subtitle}</p>

      {mode === 'code' && availableMethods.length > 1 && (
        <div className="inline-flex items-center gap-0.5 rounded-md p-0.5 bg-[rgba(55,53,47,0.06)] mb-5">
          {(['totp', 'email'] as const).filter((m) => availableMethods.includes(m)).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMethod(m)}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors disabled:opacity-50 ${
                method === m ? 'bg-white text-[#37352F] shadow-[0_1px_2px_rgba(15,15,15,0.08)]' : 'text-[#787774] hover:text-[#37352F]'
              }`}
            >
              {m === 'totp' ? <Smartphone className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
              {m === 'totp' ? 'App' : 'Email'}
            </button>
          ))}
        </div>
      )}

      {mode === 'code' ? (
        <TwoFactorCodeInputs
          code={code}
          onChange={(next) => { setCode(next); if (error) setError(null); }}
          onComplete={(full) => verify(full, method)}
          disabled={busy}
          invalid={!!error}
        />
      ) : (
        <input
          type="text"
          autoFocus
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          value={backupCode}
          onChange={(e) => { setBackupCode(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && backupCode.trim()) void verify(backupCode, 'backup'); }}
          placeholder="xxxxx-xxxxx"
          disabled={busy}
          className="block w-full px-3 py-2.5 bg-white border border-[rgba(55,53,47,0.16)] rounded-lg text-sm font-mono tracking-wider text-[#37352F] focus:outline-none focus:border-[#2383E2]"
        />
      )}

      <div className="min-h-[20px] mt-3 text-xs" aria-live="polite">
        {error ? <span className="text-red-600">{error}</span> : info && mode === 'code' && method === 'email' ? <span className="text-[#787774]">{info}</span> : null}
      </div>

      {error && connectionTips && (
        <details className="mt-1 rounded-md bg-[rgba(55,53,47,0.04)] px-3 py-2 text-xs text-[#787774]">
          <summary className="cursor-pointer select-none font-medium text-[#37352F]">Cosa puoi provare</summary>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            {CONNECTION_ERROR_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
          </ul>
          <p className="mt-2">
            Se non si risolve, scrivi a{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#2383E2] hover:underline">{SUPPORT_EMAIL}</a>{' '}
            indicando l'ora del tentativo.
          </p>
        </details>
      )}

      <label className="flex items-center gap-2 mt-2 text-sm text-[#37352F] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          disabled={busy}
          className="h-4 w-4 rounded border-[rgba(55,53,47,0.3)] text-[#37352F] focus:ring-[#2383E2]"
        />
        Ricorda questo dispositivo per 30 giorni
      </label>

      <button
        type="button"
        onClick={() => (mode === 'backup' ? verify(backupCode, 'backup') : verify(code.join(''), method))}
        disabled={busy || (mode === 'backup' ? !backupCode.trim() : code.join('').length !== 6)}
        className="notion-register-btn w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Verifica in corso...</> : 'Verifica'}
      </button>

      <div className="mt-4 flex flex-col items-center gap-2 text-sm">
        {mode === 'code' && method === 'email' && (
          <button
            type="button"
            onClick={sendEmailCode}
            disabled={busy || countdown > 0}
            className="text-[#2383E2] hover:underline disabled:text-[#787774] disabled:no-underline disabled:cursor-default"
          >
            {sendingEmail ? 'Invio in corso...' : countdown > 0 ? `Reinvia il codice tra ${countdown}s` : 'Reinvia il codice'}
          </button>
        )}
        {mode === 'code' && method === 'email' && (
          <p className="text-xs text-[#787774] text-center max-w-[300px]">
            Email non arrivata? Controlla spam e promozioni.{availableMethods.includes('totp') ? " Puoi anche usare l'app." : ''}
          </p>
        )}
        <button
          type="button"
          onClick={() => { setMode(mode === 'code' ? 'backup' : 'code'); setError(null); }}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[#787774] hover:text-[#37352F]"
        >
          <KeyRound className="w-3.5 h-3.5" />
          {mode === 'code' ? 'Usa un codice di emergenza' : 'Torna al codice a 6 cifre'}
        </button>
        <button type="button" onClick={onCancel} disabled={loading} className="text-[#787774] hover:text-[#37352F]">
          Annulla
        </button>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="fixed inset-0 bg-[rgba(15,15,15,0.4)] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg border border-[rgba(55,53,47,0.09)] p-6 w-full max-w-sm">{content}</div>
    </div>
  );
}
